import { FriendlyBooleanPipe } from '@src/app/shared/pipes/friendlyBoolean.pipe';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SessionService } from "@app/shared/services/session.service";
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { SaleReturnRequest, SalesService, RetailerResponse, RetailersService, SaleDetailsResponse, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { UserRolesService } from '@src/app/shared/services/userRoles.service';
import { Dialog } from 'primeng/dialog';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { Features } from '@src/app/shared/backend/auth/Feature';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-view-sale',
    templateUrl: './view-sale.component.html',
    styleUrl: './view-sale.component.scss',
    host: {
        class: 'app-view-sale'
    },
    providers: [FriendlyBooleanPipe, ConfirmationService, UserRolesService],
    standalone: false
})
export class ViewSaleComponent extends BaseViewComponent implements OnInit {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  showIsLegacyField: boolean = false;
  showProductAttributeDetails: boolean = false;

  allowModifySale: boolean = false;
  allowReturnSale: boolean = false;
  canReturnCurrentSale: boolean = false;
  allowViewCustomer: boolean = false;
  allowViewItem: boolean = false;
  allowViewProduct: boolean = false;
  allowViewProductCustomisation: boolean = false;
  allowViewProductAttribute: boolean = false;
  allowViewWarranty: boolean = false;
  allowViewMarket: boolean = false;
  allowViewDistributionChannel: boolean = false;
  allowViewRetailer: boolean = false;
  allowViewUser: boolean = false;
  showDistributionChannel: boolean = false;
  saleId: number = 0;
  sale: SaleDetailsResponse = {
    id: 0,
    date: '',
    isReturned: false,
    isLegacy: false,
    isGift: false,
    retailerId: 0,
    retailerName: '',
    marketId: 0,
    marketName: '',
    item: {
      id: 0,
      product: {
        id: 0,
        name: '',
        sku: ''
      }
    },
    warranty: {
      id: 0,
      code: '',
      isPrepaired: false,
      isActive: false,
      timesUsed: 0,
      useLimitReached: false,
      hasD2C: false
    },
    distributionChannel: {
      id: 0,
      name: '',
      code: ''
    }
  };
  displayReturnSaleModalbox: boolean = false;

  activeWarrantiesAccordion: number[] = [0];

  @ViewChild('returnSaleDialog') returnSaleDialog!: Dialog;
  @ViewChild('dtOperationsHistoryTable') table: Table | undefined;

  store: RetailerResponse = {};
  storesList: any[] = [];
  storesTotalRecords: number = 0;
  storesRowsPerPageOption: number = 10;
  filterOperator = FilterOperator;
  companyIsPrepaired: boolean = false;
  companyHasCpo: boolean = false;
  canViewPrice: boolean = false;
  showCustomer: boolean = false;
  allowItemOperationsSoldByAnotherRetailer: boolean = false;

  currentDate: Date = new Date();

  public saleReturnFormGroup: FormGroup = this.fb.group({
    returnDate: [this.currentDate],
    returnReason: ['', [Validators.maxLength(128)]],
    retailer: [[], [Validators.required]],
    markAsCpo: ['false', [Validators.required]]
  });

  constructor(
    public session: SessionService,
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private translate: TranslateService,
    private toasterService: ToasterService,
    private salesService: SalesService,
    private retailersService: RetailersService,) {
      super();
     }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.session.currentCompanyData$
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(company => {
        this.showIsLegacyField = company?.showIsLegacy ?? false;
        this.canViewPrice = company?.hasPrice ?? false;
        this.showProductAttributeDetails = company?.showProductAttributeDetails ?? false;
        this.companyHasCpo = !!company?.hasCpo;
        this.companyIsPrepaired = !!company?.isPrepaired;
        this.allowItemOperationsSoldByAnotherRetailer = company?.allowItemOperationsSoldByAnotherRetailer ?? false;
      });

    this.allowModifySale = await this.permissionService.hasActionPermissions(Actions.modify_sales);
    this.allowReturnSale = await this.permissionService.hasActionPermissions(Actions.return_sales);
    this.allowViewCustomer = await this.permissionService.hasActionPermissions(Actions.view_customers);
    this.allowViewItem = await this.permissionService.hasActionPermissions(Actions.view_items);
    this.allowViewProduct = await this.permissionService.hasActionPermissions(Actions.view_products);
    this.allowViewProductCustomisation = await this.permissionService.hasActionPermissions(Actions.view_productcustomisations);
    this.allowViewProductAttribute = await this.permissionService.hasActionPermissions(Actions.view_productattributedefinitions);
    this.allowViewWarranty = await this.permissionService.hasActionPermissions(Actions.view_warranties);
    this.allowViewMarket = await this.permissionService.hasActionPermissions(Actions.view_markets);
    this.allowViewDistributionChannel = await this.permissionService.hasActionPermissions(Actions.view_distributionchannels);
    this.allowViewRetailer = await this.permissionService.hasActionPermissions(Actions.view_retailers);
    this.allowViewUser = await this.permissionService.hasActionPermissions(Actions.view_users);
    this.showCustomer = await this.permissionService.hasFeaturePermissions(Features.Customers);
    this.showDistributionChannel = await this.permissionService.hasFeaturePermissions(Features.DistributionChannels);

    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async params => {
        this.saleId = Number(params['saleId']);
        if (this.saleId) {
          await this.get();
        }
      });
  }

  override async get() {
    try {
      this.sale = await firstValueFrom(
        this.salesService.apiSalesDetailsIdGet(this.saleId)
      );

      this.canReturnCurrentSale = false;

      if (this.allowReturnSale && !this.sale.isReturned) {
        const monthsResponse = await firstValueFrom(this.salesService.apiSalesReturnMonthsRetailerIdGet(this.sale.retailerId));

        const months = monthsResponse.response ?? 0; // (-1: superadmin, no restrictions)

        if (months === -1) {
          this.canReturnCurrentSale = true;
        } else {
          const now = new Date();
          const maxReturnDate = new Date(this.sale.date);

          // month restriction
          if (months > 0) {
            maxReturnDate.setMonth(maxReturnDate.getMonth() + months);
            this.canReturnCurrentSale = now <= maxReturnDate;
          } else {
            // same day return regardless of time
            this.canReturnCurrentSale = now.getFullYear() === maxReturnDate.getFullYear()
              && now.getMonth() === maxReturnDate.getMonth()
              && now.getDate() === maxReturnDate.getDate();
          }
        }
      }

      this.cdr.detectChanges();
    } catch (error) {
      console.log('Error fetching sale data:', error);
    }
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  goToEditSale() {
    this.router.navigate([`/sales/${this.saleId}/edit`]);
  }

  onTriggerSaleReturn(event: any) {
    this.onHideSaleReturnAction();
    this.displayReturnSaleModalbox = true;
    this.getStores({ pageIndex: 1, pageSize: this.storesRowsPerPageOption, search: '' });
  }

  onHideSaleReturnAction() {
    this.currentDate = new Date();

    this.saleReturnFormGroup.reset({
      returnDate: this.currentDate,
      returnReason: '',
      retailer: [],
      markAsCpo: 'false'
    });

    this.saleReturnFormGroup.markAsPristine();
    this.saleReturnFormGroup.markAsUntouched();
  }

  onLazyLoadStores(event: any) {
    this.getStores(event);
  }

  async getStores(event: any) {
    const filters: any[] = [];
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, event?.pageIndex ?? 1);

    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.storesRowsPerPageOption
    }

    if (search) {
      payload.textFilter = search;
    }

    // only active retailers
    filters.push({
      propertyName: 'isActive',
      operator: 'eq',
      values: ['true']
    });
  
    filters.push({
      propertyName: 'brands.id',
      operator: 'in',
      values: [this.sale.item.product.brandId?.toString()]
    });

    if(!this.allowItemOperationsSoldByAnotherRetailer)
    {
      filters.push({
            propertyName: 'id',
            operator: 'eq',
            values: [this.sale.retailerId.toString()]
          });
    }

    payload.additionalFilters = filters;

    const storesPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(payload));
    this.storesList = storesPage.results ?? [];
    this.storesTotalRecords = storesPage.totalCount ?? 0;

    this.preselectRetailer();
  }

  private preselectRetailer():void {
    const storesDropdownControl = this.saleReturnFormGroup.controls['retailer'];
    const selectedStore = storesDropdownControl.value;

    if (this.storesTotalRecords === 1 && this.storesList.length === 1) {
      const retailerId = this.storesList[0]?.id;
      storesDropdownControl.setValue([retailerId]);
    }
  }

  async onSaleReturn() {
    if (this.saleReturnFormGroup.valid) {

      const returnDate: Date = this.saleReturnFormGroup.controls['returnDate'].value ?? new Date();
      const formattedDate = returnDate.toISOString();

      const payload: SaleReturnRequest = {
        itemId: this.sale.item.id ?? 0,
        returnDate: formattedDate,
        returnRetailerId: this.saleReturnFormGroup.controls['retailer'].value?.[0],
        comment: this.saleReturnFormGroup.controls['returnReason'].value,
        markAsCpo: this.saleReturnFormGroup.controls['markAsCpo'].value === 'true' ? true : false,
      };

      await firstValueFrom(this.salesService.apiSalesReturnPost(payload));
      this.displayReturnSaleModalbox = false;

      this.toasterService.show('success',
        this.translate.instant('SALES.TOAST_SUCCESS_TITLE_RETURN_SUCCESS'),
        this.translate.instant('SALES.TOAST_SUCCESS_MESSAGE_RETURN_SUCCESS')
      );

      this.saleReturnFormGroup.reset();
      this.saleReturnFormGroup.setValue({
        returnDate: this.currentDate,
        returnReason: '',
        retailer: [],
        markAsCpo: 'false'
      });

      this.onHideSaleReturnAction();
      await this.get();
    }
  }

  onCancelSaleReturn() {
    this.displayReturnSaleModalbox = false;
    this.onHideSaleReturnAction();
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
